import { memo, useCallback } from 'react';

import { ArrowUpIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { cn } from '@air/utils-tailwind';

import { agenticStartTaskSegment } from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/workspaceAgentStartTaskButtonVariants';
import agenticStyles from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/workspaceAgentStartTaskCta.module.css';
import {
  WorkspaceAgentPromptCardImageStack,
  type WorkspaceAgentPromptCardThumbnail,
} from '~/components/v2/components/workspace/workspace-agent-panel/design-system/prompt-cards/WorkspaceAgentPromptCardImageStack';

const PROMPT_CARD_BUTTON_SUFFIX = <ArrowUpIcon aria-hidden size="xs" />;

export type WorkspaceAgentPromptCardProps = {
  buttonLabel: string;
  id: string;
  isDisabled: boolean;
  onSelect: (id: string) => void;
  subtitle: string;
  /** Front first. Fewer than the stack holds leaves gray placeholder tiles. */
  thumbnails: readonly WorkspaceAgentPromptCardThumbnail[];
  title: string;
};

/**
 * Title, subtitle, a stack of thumbnails, and one button. Every card is the same height so a carousel
 * reads as a row. The button's accessible name carries the title, so repeated verbs stay distinct.
 */
export const WorkspaceAgentPromptCard = memo(
  ({ buttonLabel, id, isDisabled, onSelect, subtitle, thumbnails, title }: WorkspaceAgentPromptCardProps) => {
    const handleClick = useCallback(() => onSelect(id), [id, onSelect]);

    return (
      <div
        className="@container flex h-40 gap-3 rounded-12 border ads:border-subtle border-gray-5 ads:bg-raised bg-gray-1 p-4"
        data-prompt-card-id={id}
        data-testid="WORKSPACE_AGENT_PROMPT_CARD"
      >
        <div className="flex min-w-0 flex-1 flex-col">
          <p className="line-clamp-2 font-semibold ads:text-primary text-16 text-gray-12 leading-6">{title}</p>
          <p className="mt-1 line-clamp-3 ads:text-secondary text-12 text-gray-11 leading-4">{subtitle}</p>
          <span className={cn(agenticStyles.agenticBlueCta, 'mt-auto inline-flex self-start rounded-4')}>
            <Button
              appearance="filled"
              aria-label={`${buttonLabel}: ${title}`}
              className={agenticStartTaskSegment()}
              color="blue"
              data-testid="WORKSPACE_AGENT_PROMPT_CARD_BUTTON"
              disabled={isDisabled}
              onClick={handleClick}
              size="small"
              suffix={PROMPT_CARD_BUTTON_SUFFIX}
            >
              {buttonLabel}
            </Button>
          </span>
        </div>
        {/* Below this width the thumbnails would squeeze the copy to a few characters a line. */}
        <div aria-hidden className="@[13rem]:block hidden shrink-0">
          <WorkspaceAgentPromptCardImageStack thumbnails={thumbnails} />
        </div>
      </div>
    );
  },
);

WorkspaceAgentPromptCard.displayName = 'WorkspaceAgentPromptCard';
